import React, { useState, lazy, Suspense, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { SttTabs, SttContainerTitle } from '@stt-componentes/core-v2';
import { makeStyles } from '@material-ui/core/styles';
import { Funcionalidade, TipoVinculo } from '@stt-gestao-pacs/util';
import { usuarioSignal } from '../../../signals/usuario';

const useStyles = makeStyles((theme) => ({
    wrapper: {
        padding: theme.spacing(1),
    },
}));

const Visualizador = lazy(() => import('./visualizador/Visualizador'));
const ServidorPacs = lazy(() => import('./servidor-pacs/ServidorPacs'));
const Apps = lazy(() => import('./apps/Apps'));
const Mobile = lazy(() => import('./mobile/Mobile'));
const TokensPessoais = lazy(() => import('./tokens-pessoais/TokensPessoais'));

const VisualizadorSuspense = memo((props) => {
    return (
        <Suspense>
            <SttContainerTitle titulo="DICOM HUB/VISUALIZADOR" />
            <Visualizador {...props} />
        </Suspense>
    );
});

const ServidorPacsSuspense = memo((props) => {
    return (
        <Suspense>
            <SttContainerTitle titulo="DICOM HUB/SERVIDOR PACS" />
            <ServidorPacs {...props} />
        </Suspense>
    );
});

const AppsSuspense = memo((props) => {
    return (
        <Suspense>
            <SttContainerTitle titulo="DICOM HUB/APPS" />
            <Apps {...props} />
        </Suspense>
    );
});

const MobileSuspense = memo((props) => {
    return (
        <Suspense>
            <SttContainerTitle titulo="DICOM HUB/MINIPACS MOBILE" />
            <Mobile {...props} />
        </Suspense>
    );
});

const TokensPessoaisSuspense = memo((props) => (
    <Suspense>
        <SttContainerTitle titulo="DICOM HUB/TOKENS PESSOAIS" />
        <TokensPessoais {...props} />
    </Suspense>
));

// Deep link `?aba=<nome>`. Mapa único porque as abas agora são irmãs: os valores
// `visualizador`, `servidor-pacs` e `apps` vinham do antigo nível de desktop e
// `mobile` do nível de cima — todos continuam valendo, porque quem os emite está
// fora deste repo (o Visualizador instalado na estação abre
// `?aba=visualizador&bridge=<id>&acao=configurar`, ADR-0012). Valor desconhecido —
// `desktop`, por exemplo — ou aba que o perfil não tem cai no índice 0.
const ABA_POR_QUERY = {
    apps: 'AppsSuspense',
    visualizador: 'VisualizadorSuspense',
    'servidor-pacs': 'ServidorPacsSuspense',
    mobile: 'MobileSuspense',
    'tokens-pessoais': 'TokensPessoaisSuspense',
};

const IndexDicomHub = () => {
    const classes = useStyles();
    const location = useLocation();
    const rbac = usuarioSignal.value?.rbac;
    const isAdmin = rbac?.montarAcesso(Funcionalidade.MODULO)?.tipoVinculo === TipoVinculo.ADMIN;

    const abas = [];
    if (rbac?.temPermissao(Funcionalidade.PAGINA_DICOM_HUB) && isAdmin) {
        abas.push({
            titulo: 'Apps',
            conteudo: AppsSuspense,
            key: 'AppsSuspense',
            permanente: true,
        });
    }
    // A aba Visualizador vale por PAGINA_VISUALIZADOR ([ADMIN, INSTITUICAO]): o técnico
    // de instituição administra o parque da própria instituição (ADR-0003). A aba Apps
    // continua sob PAGINA_DICOM_HUB e ADMIN-only — não afrouxa.
    if (rbac?.temPermissao(Funcionalidade.PAGINA_VISUALIZADOR)) {
        abas.push({
            titulo: 'Visualizador',
            conteudo: VisualizadorSuspense,
            key: 'VisualizadorSuspense',
            permanente: true,
        });
    }
    if (rbac?.temPermissao(Funcionalidade.PAGINA_SERVIDOR_PACS)) {
        abas.push({
            titulo: 'Servidor PACS',
            conteudo: ServidorPacsSuspense,
            key: 'ServidorPacsSuspense',
            permanente: true,
        });
    }
    // A nova entrada DICOM_HUB_APP dá acesso somente a Tokens pessoais. Mobile
    // permanece para quem já podia entrar no DicomHub; o download do APK continua
    // público (ADR-0013) e a seção administrativa continua sob TipoVinculo.ADMIN.
    const acessoDicomHubAnterior = [Funcionalidade.PAGINA_DICOM_HUB, Funcionalidade.PAGINA_SERVIDOR_PACS, Funcionalidade.PAGINA_VISUALIZADOR].some(
        (funcionalidade) => rbac?.temPermissao(funcionalidade)
    );
    if (acessoDicomHubAnterior) {
        abas.push({
            titulo: 'Minipacs Mobile',
            conteudo: MobileSuspense,
            key: 'MobileSuspense',
            permanente: true,
        });
    }

    if (rbac?.temPermissao(Funcionalidade.DICOM_HUB_APP)) {
        abas.push({
            titulo: 'Tokens pessoais',
            conteudo: TokensPessoaisSuspense,
            key: 'TokensPessoaisSuspense',
            permanente: true,
        });
    }

    const chaveDaQuery = ABA_POR_QUERY[new URLSearchParams(location.search).get('aba')];
    const abaInicial = chaveDaQuery
        ? Math.max(
              abas.findIndex((aba) => aba.key === chaveDaQuery),
              0
          )
        : 0;
    const [abaAtiva, setAbaAtiva] = useState(String(abaInicial));

    return <SttTabs abas={abas} className={classes.wrapper} handleChangeAbaAtiva={setAbaAtiva} abaAtiva={abaAtiva} permanente={true} preload={false} />;
};

export default IndexDicomHub;
